/**
 * Phone/password + biometric row (credential entry sheet body).
 * **Used by:** `LoginSheetContent`, `RegisterSheetContent` (and related auth sheet bodies re-exported from `authSheetStyles`).
 * Medical nutrition disclaimer strip: App Store 1.4.1 — visible on login/register sheets.
 * @see ../atomicRules.ts
 */
import { Platform, StyleSheet } from 'react-native';
import { appTheme } from '../../appTheme';
import type { ThemeColors } from '../../store';
import { androidStripElevationSeams } from '../../blurParity';
import { buttonWrap, createSolidPrimaryCtaSurfaceStyle } from '../buttons';

/** Must match `createSolidPrimaryCtaSurfaceStyle` height in `styles/buttons.ts` (compact credential pill). */
export const CREDENTIAL_PILL_CTA_HEIGHT = 46;

/** Half of `CREDENTIAL_PILL_CTA_HEIGHT` — new `Clickable` / `contentBorderRadius` for the round biometric target. */
export const CREDENTIAL_PILL_BIOMETRIC_RADIUS = CREDENTIAL_PILL_CTA_HEIGHT / 2;

/** Solid primary pill without Android elevation seams (credential login/register CTAs). */
export function createCredentialPrimaryCtaSurfaceStyle(c: ThemeColors) {
  return {
    ...createSolidPrimaryCtaSurfaceStyle(c),
    ...(Platform.OS === 'android' ? androidStripElevationSeams() : {}),
  };
}

/** Register primary CTA shadow host — iOS keeps depth; Android uses flat surface to avoid press seams. */
export function credentialFormPrimaryButtonWrap(c: ThemeColors) {
  if (Platform.OS === 'android') {
    return {
      alignSelf: 'stretch' as const,
      flexShrink: 0,
      ...androidStripElevationSeams(),
    };
  }
  return {
    ...buttonWrap,
    shadowColor: c.shadowColor,
  };
}

function credentialBiometricCtaIconRoot(c: ThemeColors) {
  const shared = {
    width: CREDENTIAL_PILL_CTA_HEIGHT,
    height: CREDENTIAL_PILL_CTA_HEIGHT,
    borderRadius: CREDENTIAL_PILL_BIOMETRIC_RADIUS,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };
  if (Platform.OS === 'android') {
    return {
      ...shared,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.primaryHalf,
      backgroundColor: c.surface,
      ...androidStripElevationSeams(),
    };
  }
  return {
    ...shared,
    borderWidth: 0.5,
    borderColor: c.primaryHalf,
    backgroundColor: c.surface,
    ...appTheme.shadows.button,
    shadowColor: c.shadowColor,
  };
}

export function createCredentialFormSheetStyles(c: ThemeColors) {
  return StyleSheet.create({
    /**
     * Intrinsic shell for center shrink-wrap cards — never `flex: 1` (collapses to 0 when parent
     * has no bounded height). Tall content is capped via `useAuthFormScrollMaxHeight` px maxHeight.
     */
    keyboardView: { width: '100%', flexGrow: 0, flexShrink: 1, backgroundColor: c.surface },
    scroll: { width: '100%', flexGrow: 0, flexShrink: 1, backgroundColor: c.surface },
    scrollContent: {
      backgroundColor: c.surface,
      flexGrow: 0,
      paddingHorizontal: appTheme.spacing.lg,
      maxWidth: 440,
      width: '100%',
      alignSelf: 'center',
    },
    fields: {
      flexDirection: 'column',
      gap: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
    },
    /** Nudge vs vertical center so `+` aligns with phone `TextInput` text. Use margin (not transform) so `Input` prefix wrapper can animate. */
    countryCodeText: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
      letterSpacing: 0.2,
      lineHeight: 21,
      textAlign: 'center',
      marginTop: Platform.select({ ios: -3, android: -2, default: 0 }),
    },
    /**
     * Single child of new `Clickable` (forgot link) — layout + tap bounds; label uses `forgotText`.
     */
    forgotActionRoot: {
      alignSelf: 'flex-end',
      marginBottom: 0,
      marginTop: appTheme.spacing.xs,
    },
    forgotText: {
      ...appTheme.typography.bodyMedium,
      color: c.primary,
    },
    signupRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: appTheme.spacing.xs,
      
    },
    buttonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.xs,
    },
    /**
     * Full-width row: flex-1 primary pill + fixed square biometric. `width: '100%'` so the pill fills space next to the icon.
     */
    primaryWithBiometricRow: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      /** Was `xs` — a touch more air between the login pill and biometric control. */
      gap: appTheme.spacing.sm,
      paddingVertical: 0,
    },
    /**
     * New `Clickable` wrapper in `primaryWithBiometricRow` — row flex slot so the pill uses remaining width.
     */
    primaryCtaRowSlot: {
      flex: 1,
      minWidth: 0,
    },
    /** Width fill on top of `createSolidPrimaryCtaSurfaceStyle` (same view as pill). */
    primaryCtaSurfaceFill: {
      width: '100%',
    },
    /**
     * Sole child of new `Clickable` for biometric — fixed circle, same height as the credential solid primary pill.
     */
    biometricCtaIconRoot: credentialBiometricCtaIconRoot(c),
    /** `Clickable2` biometric slot — flex slot only (dimensions via `minWidth`/`minHeight` on `Clickable2`). */
    biometricCtaClickable2: {
      flexShrink: 0,
    },
    /**
     * Sole child of new `Clickable` for the sign-up link (row with `signupText` sibling).
     */
    signUpActionRoot: {
      flexShrink: 0,
    },
    signupRowPad: {
      paddingBottom: appTheme.spacing.xs,
    },
    signupRowMainScreen: {
      paddingBottom: 0,
    },
    /** Tighter rhythm for MainScreen bottom-sheet auth forms. */
    mainScreenScrollContent: {
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.sm,
    },
    mainScreenFields: {
      gap: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
    },
    mainScreenForgotActionRoot: {
      alignSelf: 'flex-end',
      marginBottom: appTheme.spacing.sm,
      marginTop: 0,
    },
    mainScreenPrimaryRow: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      paddingVertical: 0,
    },
    signupText: { ...appTheme.typography.bodySmall, color: c.textSecondary },
    signupLink: { ...appTheme.typography.bodyMedium, color: c.primary },
    /** App Store Guideline 1.4.1 — general wellness / meal-planning disclaimer + link to references. */
    medicalNutritionDisclaimerBanner: {
      width: '100%',
      padding: appTheme.spacing.md,
      marginBottom: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: c.surfaceLight,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.border,
    },
    medicalNutritionDisclaimerText: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      lineHeight: 20,
    },
    medicalNutritionDisclaimerLink: {
      ...appTheme.typography.bodySmall,
      fontWeight: '700',
      color: c.primary,
      marginTop: appTheme.spacing.sm,
    },
  });
}

export type CredentialFormSheetStyles = ReturnType<typeof createCredentialFormSheetStyles>;
